import { useEffect, useRef, useState } from "react";
import { ApiError } from "@kundeninfo/api-client";
import type { Belegvorschau, LayoutVorschauAnfrage } from "@kundeninfo/api-client";

/**
 * Die VORSCHAU des Layout-Editors (Spec R3): „Aktualisieren" rendert sofort, das Verlassen
 * eines Felds entprellt (`verzoegerungMs` Ruhe). Höchstens EINE Anfrage läuft je Layout;
 * kommt währenddessen eine neue, wartet nur die jüngste, und die Antwort der laufenden
 * wird verworfen — sie zeigte einen Stand, den niemand mehr meint.
 */

export type VorschauFehler = { text: string; felder: { field: string; message: string }[] };

export type LayoutVorschau = {
  stand: Belegvorschau | null;
  laedt: boolean;
  fehler: VorschauFehler | null;
  anfordern: (auftrag: LayoutVorschauAnfrage) => void;
  sofort: (auftrag: LayoutVorschauAnfrage) => void;
};

export function useLayoutVorschau(
  laden: (auftrag: LayoutVorschauAnfrage) => Promise<Belegvorschau>, verzoegerungMs = 600,
): LayoutVorschau {
  const [stand, setStand] = useState<Belegvorschau | null>(null);
  const [laedt, setLaedt] = useState(false);
  const [fehler, setFehler] = useState<VorschauFehler | null>(null);
  const ladenRef = useRef(laden);
  ladenRef.current = laden;
  const uhr = useRef<ReturnType<typeof setTimeout> | null>(null);
  const laeuft = useRef(false);
  const wartend = useRef<LayoutVorschauAnfrage | null>(null);
  const lebt = useRef(true);

  useEffect(() => {
    lebt.current = true;
    return () => {
      lebt.current = false;
      if (uhr.current !== null) clearTimeout(uhr.current);
    };
  }, []);

  function starte(auftrag: LayoutVorschauAnfrage): void {
    if (laeuft.current) {
      wartend.current = auftrag;
      return;
    }
    laeuft.current = true;
    setLaedt(true);
    ladenRef.current(auftrag).then((neu) => {
      if (!lebt.current || wartend.current !== null) return;
      setStand(neu);
      setFehler(null);
    }, (f: unknown) => {
      if (!lebt.current || wartend.current !== null) return;
      setFehler(f instanceof ApiError
        ? { text: f.message, felder: f.fields ?? [] }
        : { text: "Die Vorschau konnte nicht erstellt werden.", felder: [] });
    }).finally(() => {
      if (!lebt.current) return;
      laeuft.current = false;
      const naechster = wartend.current;
      wartend.current = null;
      if (naechster !== null) starte(naechster);
      else setLaedt(false);
    });
  }

  return {
    stand, laedt, fehler,
    anfordern: (auftrag) => {
      if (uhr.current !== null) clearTimeout(uhr.current);
      uhr.current = setTimeout(() => { uhr.current = null; starte(auftrag); }, verzoegerungMs);
    },
    sofort: (auftrag) => {
      if (uhr.current !== null) clearTimeout(uhr.current);
      uhr.current = null;
      starte(auftrag);
    },
  };
}
